/**
 * Estado "desconectado": login e cadastro em duas etapas.
 * Estrutura e medidas: docs/spec/screens/TfrmDisconnectedStateForm.md
 *
 *  panFloater 714×427
 *  ├─ panFloaterIcon 312 (marca)
 *  └─ panFloaterClient 402
 *     ├─ layTopContent 57 (idioma)
 *     ├─ layLoginLayout | layRegistrationLayout (step1 / step2) 317
 *     └─ panBottomContent 53 (criar conta / voltar)
 */
import { useState, type CSSProperties, type KeyboardEvent } from 'react';
import { api, type GrammaticalGender, type RegisterFieldError } from '../api.js';
import { LANGUAGES, useLanguage, useT } from '../i18n/index.js';
import type { LegalDoc } from '../legal/documents.js';
import { useApp } from '../store.js';
import { Button, CheckBox, ComboBox, Dialog, ErrorLabel, TextEdit } from '../ui/controls.js';
import { Icon, RpgDeskMark } from '../ui/icons.js';
import { TermsDialog } from './TermsDialog.js';
import { TosConsent } from './TosConsent.js';
import './disconnected.css';

export type Mode = 'login' | 'step1' | 'step2';

/**
 * Posição absoluta no layout do form de referência (402×317 no design). Em
 * execução o grid do original alarga a área para 450 px e escala só na
 * horizontal; reproduzimos isso com a unidade `--rd-dx` (disconnected.css), que
 * é 1/402 da largura da área: no celular, a área tem a largura da tela, e o
 * formulário se estreita junto.
 */
const at = (x: number, y: number, w?: number, h?: number): CSSProperties => ({
  position: 'absolute',
  left: `calc(${x} * var(--rd-dx))`,
  top: y,
  ...(w !== undefined ? { width: `calc(${w} * var(--rd-dx))` } : {}),
  ...(h !== undefined ? { height: h } : {}),
});

/**
 * `initialMode`: o site (docs/spec/landing.md) abre o entrar ou o cadastro. `onSite`: o link
 * "Conhecer o RPG Desk", de volta ao site, quando ele cabe (fora do app de Windows e do instalado).
 */
export function DisconnectedState({ initialMode = 'login', onSite }: { initialMode?: Mode; onSite?: (() => void) | undefined } = {}) {
  const t = useT();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [dialog, setDialog] = useState<'forgot' | null>(null);
  // os termos ou a política, abertos pelos links da caixa do aceite
  const [legal, setLegal] = useState<LegalDoc | null>(null);
  // um administrador encerrou a sessão (docs/spec/admin.md): o porquê, antes de entrar de novo
  const signedOut = useApp((s) => s.signedOutNotice);
  const dismissSignedOut = useApp((s) => s.dismissSignedOutNotice);
  // o link do e-mail da confirmação aberto sem a conta neste navegador (docs/spec/email.md)
  const emailLink = useApp((s) => s.emailLinkNotice);
  const dismissEmailLink = useApp((s) => s.dismissEmailLinkNotice);
  // com uma conta salva, o /api/me ainda está respondendo: o aviso sai como aviso rápido, na conta
  const savedAccount = useApp((s) => !!s.token);

  return (
    <div className="rd-disconnected">
      {onSite ? (
        <button type="button" className="rd-disconnected__site" onClick={onSite}>
          <Icon name="chevronLeft" size={16} />
          {t('landing.back')}
        </button>
      ) : null}
      <div className="rd-disconnected__floater">
        <div className="rd-disconnected__icon">
          <RpgDeskMark size={132} />
          <div className="rd-disconnected__brand">{t('app.name')}</div>
          <div className="rd-disconnected__tagline">{t('app.tagline')}</div>
        </div>

        <div className="rd-disconnected__client">
          <div className="rd-disconnected__top">
            <LanguageButton />
          </div>

          <div className="rd-disconnected__center">
            {mode === 'login' ? (
              <LoginLayout onForgot={() => setDialog('forgot')} />
            ) : (
              <RegistrationLayout step={mode} onStep={setMode} onViewTos={setLegal} />
            )}
          </div>

          <div className="rd-disconnected__bottom">
            {mode === 'login' ? (
              <Button variant="link" style={{ width: 360, height: 51 }} onClick={() => setMode('step1')}>
                {t('login.register')}
              </Button>
            ) : (
              <Button variant="link" style={{ width: 360, height: 51 }} onClick={() => setMode('login')}>
                {t('register.cancel')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {dialog === 'forgot' ? (
        <Dialog
          title={t('forgot.title')}
          onClose={() => setDialog(null)}
          actions={
            <Button variant="action" onClick={() => setDialog(null)}>
              {t('common.ok')}
            </Button>
          }
        >
          {t('forgot.text')}
        </Dialog>
      ) : null}
      {signedOut ? (
        <Dialog
          title={t(`signedOut.${signedOut.reason}.title`)}
          onClose={dismissSignedOut}
          actions={
            <Button variant="action" onClick={dismissSignedOut}>
              {t('common.ok')}
            </Button>
          }
        >
          <p className="rd-prompt__label">{t(`signedOut.${signedOut.reason}.text`)}</p>
          {signedOut.detail ? <p className="rd-prompt__label">{t('signedOut.reason', { reason: signedOut.detail })}</p> : null}
        </Dialog>
      ) : null}
      {emailLink && !signedOut && !savedAccount ? (
        <Dialog
          title={t(`confirmEmail.link.${emailLink}.title`)}
          onClose={dismissEmailLink}
          actions={
            <Button variant="action" onClick={dismissEmailLink}>
              {t('common.ok')}
            </Button>
          }
        >
          <p className="rd-prompt__label">{t(`confirmEmail.link.${emailLink}`)}</p>
        </Dialog>
      ) : null}
      {legal ? <TermsDialog initial={legal} onClose={() => setLegal(null)} /> : null}
    </div>
  );
}

/** O idioma da tela, no alto da caixa (também na tela de espera da confirmação do e-mail). */
export function LanguageButton() {
  const t = useT();
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find((l) => l.id === language)?.label ?? language;
  return (
    <div style={{ position: 'relative' }}>
      <Button
        variant="linkDropdown"
        style={{ width: 329, height: 33 }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t('common.language')}: ${current}`}
        onClick={() => setOpen((v) => !v)}
      >
        {current}
      </Button>
      {open ? (
        <div className="rd-menu" role="menu" style={{ left: 74, top: 36 }} onMouseLeave={() => setOpen(false)}>
          {LANGUAGES.map((l) => (
            <button
              key={l.id}
              type="button"
              role="menuitemradio"
              aria-checked={l.id === language}
              className="rd-menu__item"
              onClick={() => {
                setLanguage(l.id);
                setOpen(false);
              }}
            >
              {l.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ login */

function LoginLayout({ onForgot }: { onForgot: () => void }) {
  const t = useT();
  const reveal = { show: t('login.showPassword'), hide: t('login.hidePassword') };
  const login = useApp((s) => s.login);
  const busy = useApp((s) => s.busy);
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [keep, setKeep] = useState(true);
  const [accountError, setAccountError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  async function submit() {
    const a = account.trim() ? '' : t('login.error.accountRequired');
    const p = password ? '' : t('login.error.passwordRequired');
    setAccountError(a);
    setPasswordError(p);
    if (a || p) return;
    const ok = await login(account.trim(), password, keep);
    if (!ok) {
      const { error, dismissError } = useApp.getState();
      setPasswordError(error ?? t('login.error.failed'));
      dismissError();
    }
  }

  const next = (e: KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  return (
    <div className="rd-disconnected__layout">
      <div className="rd-disconnected__welcome" style={at(48, 16, 306)}>
        {t('login.welcome')}
      </div>
      <TextEdit
        style={at(50, 42, 300, 40)}
        icon="user"
        value={account}
        onChange={setAccount}
        placeholder={t('login.account')}
        aria-label={t('login.account')}
        autoComplete="username"
        invalid={!!accountError}
        autoFocusOnMount
        onKeyDown={(e) => next(e, () => document.getElementById('rd-login-password')?.focus())}
      />
      <ErrorLabel style={at(49, 84, 300)}>{accountError}</ErrorLabel>
      <TextEdit
        id="rd-login-password"
        style={at(50, 114, 300, 40)}
        icon="lock"
        type="password"
        reveal={reveal}
        value={password}
        onChange={setPassword}
        placeholder={t('login.password')}
        aria-label={t('login.password')}
        autoComplete="current-password"
        invalid={!!passwordError}
        onKeyDown={(e) => next(e, () => void submit())}
      />
      <ErrorLabel style={at(51, 157, 300)}>{passwordError}</ErrorLabel>
      <Button variant="action" style={at(58, 194, 300, 40)} busy={busy} onClick={() => void submit()}>
        {t('login.submit')}
      </Button>
      <div style={at(16, 248, 377, 50)}>
        <CheckBox checked={keep} onChange={setKeep} style={at(8, 7, 160, 34)}>
          {t('login.keepSignedIn')}
        </CheckBox>
        <Button variant="strongLink" style={at(191, 1, 162, 41)} onClick={onForgot}>
          {t('login.forgotPassword')}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ cadastro */

type Step1Errors = { email?: string; account?: string; gender?: string };
type Step2Errors = { password?: string; confirm?: string };

function RegistrationLayout({ step, onStep, onViewTos }: { step: 'step1' | 'step2'; onStep: (m: Mode) => void; onViewTos: (doc: LegalDoc) => void }) {
  const t = useT();
  const reveal = { show: t('login.showPassword'), hide: t('login.hidePassword') };
  const register = useApp((s) => s.register);
  const busy = useApp((s) => s.busy);
  const [email, setEmail] = useState('');
  const [account, setAccount] = useState('');
  const [gender, setGender] = useState<GrammaticalGender | ''>('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [promo, setPromo] = useState(false);
  const [tos, setTos] = useState(false);
  const [errors1, setErrors1] = useState<Step1Errors>({});
  const [errors2, setErrors2] = useState<Step2Errors>({});
  const [validating, setValidating] = useState(false);
  const [tosError, setTosError] = useState(false);

  const fieldError = (field: 'displayName' | 'email', code?: RegisterFieldError) => {
    if (!code) return undefined;
    if (code === 'invalid' || code === 'taken') return t(`register.error.${code}.${field}`);
    return t(`register.error.${code}`);
  };

  async function gotoStep2() {
    const local: Step1Errors = {};
    if (!gender) local.gender = t('register.error.gender');
    setValidating(true);
    try {
      const res = await api.validateRegistration(account, email);
      const e: Step1Errors = {
        ...local,
        ...(res.errors.email ? { email: fieldError('email', res.errors.email) } : {}),
        ...(res.errors.displayName ? { account: fieldError('displayName', res.errors.displayName) } : {}),
      };
      setErrors1(e);
      if (Object.keys(e).length === 0) onStep('step2');
    } catch {
      setErrors1({ ...local, account: t('login.connectionProblem') });
    } finally {
      setValidating(false);
    }
  }

  async function finish() {
    const e: Step2Errors = {};
    if (!password) e.password = t('register.error.passwordEmpty');
    else if (password !== confirm) e.confirm = t('register.error.passwordMismatch');
    setErrors2(e);
    setTosError(!tos);
    if (Object.keys(e).length || !tos || !gender) return;
    const ok = await register(
      { displayName: account.trim(), email: email.trim(), password, grammaticalGender: gender, wantPromoMail: promo, acceptTos: true },
      true,
    );
    if (!ok) {
      const { error, dismissError } = useApp.getState();
      setErrors2({ password: error ?? t('register.error.failed') });
      dismissError();
    }
  }

  const onEnter = (action: () => void) => (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  if (step === 'step1') {
    return (
      <div className="rd-disconnected__layout">
        <div className="rd-disconnected__welcome" style={at(56, 24, 300)}>
          {t('register.welcome')}
        </div>
        <TextEdit
          style={at(58, 58, 300, 40)}
          icon="mail"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder={t('register.email')}
          aria-label={t('register.email')}
          autoComplete="email"
          invalid={!!errors1.email}
          autoFocusOnMount
          onKeyDown={onEnter(() => document.getElementById('rd-reg-account')?.focus())}
        />
        <ErrorLabel style={at(57, 100, 300)}>{errors1.email}</ErrorLabel>
        <TextEdit
          id="rd-reg-account"
          style={at(58, 130, 300, 40)}
          icon="user"
          value={account}
          onChange={setAccount}
          placeholder={t('register.account')}
          aria-label={t('register.account')}
          autoComplete="username"
          invalid={!!errors1.account}
          onKeyDown={onEnter(() => void gotoStep2())}
        />
        <ErrorLabel style={at(57, 172, 300)}>{errors1.account}</ErrorLabel>
        <ComboBox<GrammaticalGender>
          style={at(56, 192, 305, 40)}
          value={gender}
          onChange={setGender}
          placeholder={t('register.gender')}
          aria-label={t('register.gender')}
          invalid={!!errors1.gender}
          options={[
            { value: 'masculine', label: t('register.gender.masculine') },
            { value: 'feminine', label: t('register.gender.feminine') },
            { value: 'neutral', label: t('register.gender.neutral') },
          ]}
        />
        <ErrorLabel style={at(57, 236, 300)}>{errors1.gender}</ErrorLabel>
        <Button variant="action" style={at(58, 258, 300, 40)} busy={validating} onClick={() => void gotoStep2()}>
          {t('register.continue')}
        </Button>
      </div>
    );
  }

  return (
    <div className="rd-disconnected__layout">
      <div style={at(24, 8, 361, 41)}>
        <Button variant="linkAction" icon="chevronLeft" style={at(16, 2, 49, 36)} aria-label={t('register.back')} onClick={() => onStep('step1')} />
        <div className="rd-disconnected__welcome" style={at(81, 10, 265)}>
          {t('register.step2.welcome')}
        </div>
      </div>
      <TextEdit
        style={at(66, 58, 300, 40)}
        icon="lock"
        type="password"
        reveal={reveal}
        value={password}
        onChange={setPassword}
        placeholder={t('register.password')}
        aria-label={t('register.password')}
        autoComplete="new-password"
        invalid={!!errors2.password}
        autoFocusOnMount
        onKeyDown={onEnter(() => document.getElementById('rd-reg-confirm')?.focus())}
      />
      <ErrorLabel style={at(65, 100, 300)}>{errors2.password}</ErrorLabel>
      <TextEdit
        id="rd-reg-confirm"
        style={at(66, 122, 300, 40)}
        icon="lock"
        type="password"
        reveal={reveal}
        value={confirm}
        onChange={setConfirm}
        placeholder={t('register.confirmPassword')}
        aria-label={t('register.confirmPassword')}
        autoComplete="new-password"
        invalid={!!errors2.confirm}
        onKeyDown={onEnter(() => void finish())}
      />
      <ErrorLabel style={at(65, 164, 300)}>{errors2.confirm}</ErrorLabel>
      <CheckBox checked={promo} onChange={setPromo} alignTop style={{ ...at(72, 180, 289, 22), fontSize: 13 }}>
        {t('register.wantPromoMail')}
      </CheckBox>
      {/* a idade e os dois textos numa caixa só, em duas linhas (docs/spec/termos.md) */}
      <TosConsent
        checked={tos}
        onChange={(v) => {
          setTos(v);
          if (v) setTosError(false);
        }}
        invalid={tosError}
        onView={onViewTos}
        style={at(72, 205, 300, 36)}
      />
      {tosError ? <ErrorLabel style={at(73, 246, 300)}>{t('register.error.tosRequired')}</ErrorLabel> : null}
      <Button variant="action" style={at(66, 266, 175, 40)} busy={busy} onClick={() => void finish()}>
        {t('register.submit')}
      </Button>
    </div>
  );
}
